import type { ApprovalDecision, Payment } from '@/types/models';
import { useCurrentUser } from '../currentUser';
import { createEventHook } from '@vueuse/core';
import { usePut, useToaster } from '@foodics/ui-common';
import { paymentsApi, type ApprovePaymentRequest } from '@/api/payments';
import { isFuture } from 'date-fns';
import { computed, toValue, type Ref } from 'vue';
import { triggerPaymentUpdate } from '.';
import { PaymentStatusEnum } from '@/enums/payments';
import { useLocale } from '../locale';
import { parseError } from '@/utils/errors';

/**
 * Counts the number of unique approval levels that have been granted for a payment.
 * If multiple approvers at the same level approve, it's counted as one approval.
 * @param payment - The payment object to check approvals for
 * @returns The number of unique approval levels that have been granted
 */
export function getApprovedCount(payment: Payment) {
    const approvedApprovals = payment.approvals.filter(
        (approval) => approval.decision === 'granted',
    );

    // if two persons approved and they are on the same level, they are counted as only one approval
    const approvedLevels = new Set(
        approvedApprovals.map((approval) => approval.level),
    );

    return approvedLevels.size;
}

/**
 * Gets the total number of unique approval levels required for the payment.
 * @param payment - The payment object to check
 * @returns The total number of unique approval levels needed
 */
export function getApprovalsCount(payment: Payment) {
    return new Set(payment.approvals.map((approval) => approval.level)).size;
}

/**
 * Checks if all required approval levels have been granted for the payment.
 * @param payment - The payment object to check
 * @returns True if all required levels have approved, false otherwise
 */
export function isAllApproved(payment: Payment) {
    return getApprovedCount(payment) === getApprovalsCount(payment);
}

/**
 * Checks if no approvals have been granted yet for the payment.
 * @param payment - The payment object to check
 * @returns True if no approvals have been granted, false otherwise
 */
export function isNoneApproved(payment: Payment) {
    return getApprovedCount(payment) === 0;
}

/**
 * Checks if the payment is partially approved (some levels approved but not all).
 * @param payment - The payment object to check
 * @returns True if some but not all levels have approved, false otherwise
 */
export function isSomeApproved(payment: Payment) {
    const approvedCount = getApprovedCount(payment);
    const approvalsCount = getApprovalsCount(payment);

    return approvedCount > 0 && approvedCount < approvalsCount;
}

export function isPendingLastApproval(payment: Payment) {
    return getApprovedCount(payment) === getApprovalsCount(payment) - 1;
}
export function getRejectedBy(payment: Payment) {
    return payment.approvals.find((approval) => approval.decision === 'denied')
        ?.approver.name;
}
export function getCancelledBy(payment: Payment) {
    return payment.meta?.canceled_by?.name;
}
/**
 * Finds all pending approvers at the next level that needs attention.
 * - Sorts approvals by level in ascending order
 * - Finds the first level where:
 *   - There's at least one pending approval
 *   - No one at that level has approved yet
 * - Returns all approvers at that level
 *
 * @param payment - The payment object containing approvals
 * @returns Array of approvers at the next level that need to approve, or undefined if none found
 */
export function getNextApprovers(payment: Payment) {
    const sortedApprovals = [...payment.approvals].sort(
        (a, b) => a.level - b.level,
    );
    const nextLevel = sortedApprovals.find((approval) => {
        const isPending = approval.decision === 'pending';
        const noOneElseOnTheSameLevelApproved = !payment.approvals.some(
            (a) => a.level === approval.level && a.decision === 'granted',
        );
        return isPending && noOneElseOnTheSameLevelApproved;
    })?.level;

    return nextLevel
        ? sortedApprovals.filter((approval) => approval.level === nextLevel)
        : [];
}

const { user: currentUser } = useCurrentUser();
/**
 * Checks if a specific user is the next person who needs to approve the payment.
 * If no userId is provided, checks against the current user.
 * @param payment - The payment object to check
 * @param userId - Optional user ID to check (defaults to current user)
 * @returns True if the specified user is the next approver, false otherwise
 */
export function isNextApprover(
    payment: Payment,
    userId: string = currentUser.value!.id,
) {
    if (!userId) return false;

    const nextApprovers = getNextApprovers(payment);
    return nextApprovers.some((approval) => approval.approver.id === userId);
}

/**
 * Checks if a specific user is one of the approvers for the payment.
 * If no userId is provided, checks against the current user.
 * @param payment - The payment object to check
 * @param userId - Optional user ID to check (defaults to current user)
 * @returns True if the user is an approver for this payment, false otherwise
 */
export function isApprover(
    payment: Payment,
    userId: string = currentUser.value!.id,
) {
    if (!userId) return false;

    return payment.approvals.some(
        (approval) => approval.approver.id === userId,
    );
}

const { addToast } = useToaster();
const { t } = useLocale();
export const {
    trigger: triggerUpdateApprovalDecision,
    on: onUpdateApprovalDecision,
} = createEventHook<{
    approvalId: string;
    decision: ApprovalDecision;
}>();

/**
 * A composable function for managing the approval decision process of a payment.
 * Provides functionality to update an approval's decision (grant/deny) and handles
 * the associated UI feedback through toast messages.
 *
 * Features:
 * - Makes API call to update approval decision
 * - Shows appropriate toast messages based on decision and payment state
 * - Handles different scenarios (last approval, scheduled payments)
 * - Triggers update events for real-time UI updates
 * - Provides loading state for UI feedback
 *
 * @param approvalId - The unique identifier of the approval to be updated
 * @param payment - The payment object containing all approval information
 *
 * @returns An object containing:
 * - isLoading: Boolean indicating if an approval update is in progress
 * - updateApprovalDecision: Function to update the approval decision
 */
export const useUpdateApprovalDecision = (
    approvalId: string | Ref<string>,
    payment: Payment,
) => {
    const { isLoading, execute, onData } = usePut<
        ApprovePaymentRequest['payload'],
        ApprovePaymentRequest['response']
    >(computed(() => paymentsApi.approve(toValue(approvalId))));

    const isScheduledPayment = isFuture(payment.submit_at);

    /**
     * Constructs an appropriate toast message based on the approval decision and payment state
     * @param decision - The approval decision (granted/denied)
     * @returns A context-aware message string for the toast notification
     */
    function constructToastMessage(decision: ApprovalDecision) {
        if (decision === 'denied') {
            return t('payments.payment_approvals.rejected_moved_to_history');
        }

        //decision is granted
        if (isPendingLastApproval(payment)) {
            return isScheduledPayment
                ? t('payments.payment_approvals.approved_moved_to_scheduled')
                : t('payments.payment_approvals.approved_on_way_to_recipient');
        }

        return t('payments.payment_approvals.payment_approved');
    }

    function updatePaymentStatus(decision: ApprovalDecision) {
        if (decision === 'granted' && isPendingLastApproval(payment)) {
            const updatedStatus = isScheduledPayment
                ? PaymentStatusEnum.SCHEDULED
                : PaymentStatusEnum.REQUESTED;

            triggerPaymentUpdate([
                {
                    ...payment,
                    status: updatedStatus,
                },
                'approve',
            ]);

            return;
        }

        if (decision === 'denied') {
            triggerPaymentUpdate([
                {
                    ...payment,
                    status: PaymentStatusEnum.REJECTED,
                },
                'reject',
            ]);

            return;
        }
    }

    /**
     * Updates the approval decision for a payment and handles the UI feedback
     * @param decision - The approval decision to be set (granted/denied)
     * @param reason - Optional reason for the decision, typically required for denials
     * @returns The API response data if successful, undefined if failed
     */
    async function updateApprovalDecision(
        decision: ApprovalDecision,
        reason?: string,
    ) {
        try {
            const { data, error } = await execute({
                decision,
                reason,
            });

            if (error) throw error;
            if (!data)
                throw new Error(
                    t('payments.approval_messages.approval_update_error'),
                );

            // show the toast message
            addToast({
                title: constructToastMessage(decision),
                variant: 'success',
            });

            // update the payment status on the FE side
            updatePaymentStatus(decision);

            triggerUpdateApprovalDecision({
                approvalId: toValue(approvalId),
                decision,
            });

            return data;
        } catch (error) {
            addToast({
                title: parseError(error),
                variant: 'error',
            });
        }
    }

    return {
        isLoading,
        updateApprovalDecision,
        onUpdateApprovalFulfilled: onData,
    };
};
